import { useState, useEffect, useRef, forwardRef } from 'react';
import { IconButton } from '../iconButton';
import { Spinner } from '../spinner';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { router } from '@inertiajs/react';

let timeout;

export const ToolbarComponent = forwardRef(
  (
    { children, value, onChange, clear = () => {}, filtering = false, classes = '', placeholder },
    ref
  ) => {
    const icon = filtering ? (
      <Spinner size="sm" />
    ) : (
      <IconButton onClick={clear} icon={value !== '' ? XMarkIcon : MagnifyingGlassIcon} />
    );

    return (
      <div
        className={`mb-6 z-10 sticky top-4 toolbar shadow bg-white rounded-lg h-[64px] flex justify-between py-1 pr-6 ${classes}`}
      >
        <div className="flex items-center w-full pr-6">
          <div className="ml-6 w-6 flex-shrink-0 flex items-center">{icon}</div>
          <input
            placeholder={placeholder}
            value={value}
            ref={ref}
            onChange={onChange}
            className="h-full flex-grow rounded-lg pl-4 text-lg focus:ring-0 border-0"
          />
        </div>
        <div className="inline-flex items-center">{children}</div>
      </div>
    );
  }
);

export function Toolbar({
  children,
  filterUrl,
  autofocus = false,
  onSearch
}: {
  children: ReactNode[];
  filterUrl: string;
  autofocus?: boolean;
  onSearch?: (e: string) => void;
}) {
  const currentUrl = new URL(filterUrl || window.location.href, window.location.origin);

  const getInitialKeyword = () => {
    return currentUrl.searchParams.get('keyword') || '';
  };

  const [keyword, setKeyword] = useState(getInitialKeyword());
  const [filtering, setFiltering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autofocus]);

  const navigate = url => {
    router.visit(url.toString(), {
      method: 'get',
      onFinish: () => {
        setFiltering(false);
      }
    });
  };

  useEffect(() => {
    const currentUrlKeyword = currentUrl.searchParams.get('keyword') || '';

    if (keyword !== currentUrlKeyword) {
      clearTimeout(timeout);

      setFiltering(true);

      timeout = setTimeout(() => {
        if (keyword === '') {
          currentUrl.searchParams.delete('keyword');
        } else {
          currentUrl.searchParams.set('keyword', keyword);
        }

        router.visit(currentUrl.toString(), {
          method: 'get',
          onFinish: () => setFiltering(false)
        });
      }, 500);
    }
  }, [keyword]);

  return (
    <ToolbarComponent
      value={keyword}
      filtering={filtering}
      clear={() => setKeyword('')}
      onChange={e => setKeyword(e.target.value)}
    >
      {children}
    </ToolbarComponent>
  );
}
